import { cloneElement, isValidElement, useRef, useState, type MouseEvent, type ReactElement, type ReactNode, type Ref } from "react";
import MuiMenu from "@mui/material/Menu";
import MuiMenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import MenuList from "@mui/material/MenuList";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import type { MenuItemType } from "./Menu";

// antd <Dropdown> -> MUI. Uncontrolled (click) uses an anchor-based MUI Menu
// (modal, closes on outside click). When `open` is CONTROLLED, we render a
// non-modal Popper instead: MUI Menu's modal backdrop/focus-trap would cover a
// trigger that contains its own input (e.g. GlobalSearch's search box), blocking
// typing. The Popper keeps the trigger fully interactive.

export interface DropdownProps {
  children: ReactElement;
  overlay?: ReactNode;
  menu?: { items?: MenuItemType[]; onClick?: (info: { key: string }) => void };
  trigger?: Array<"click" | "hover" | "contextMenu">;
  placement?: string;
  disabled?: boolean;
  arrow?: boolean;
  /** antd controlled visibility. When provided, the parent drives open/close and
   *  we render a non-modal Popper. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  overlayClassName?: string;
  overlayStyle?: import("react").CSSProperties;
  getPopupContainer?: unknown;
}

const assignRef = <T,>(ref: Ref<T> | undefined, node: T | null) => {
  if (typeof ref === "function") ref(node);
  else if (ref && typeof ref === "object") (ref as { current: T | null }).current = node;
};

export const Dropdown = ({ children, overlay, menu, disabled, open: openProp, onOpenChange, overlayClassName, overlayStyle }: DropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const controlledAnchorRef = useRef<HTMLElement | null>(null);
  const isControlled = openProp !== undefined;
  const open = isControlled ? !!openProp : Boolean(anchorEl);

  const openMenu = (e: MouseEvent<HTMLElement>) => {
    if (!disabled) setAnchorEl(e.currentTarget);
  };
  const close = () => {
    if (!isControlled) setAnchorEl(null);
  };

  const renderMenuItems = () =>
    menu!.items!.map((it) => (
      <MuiMenuItem
        key={String(it.key)}
        disabled={it.disabled}
        onClick={() => {
          it.onClick?.();
          menu!.onClick?.({ key: String(it.key) });
          close();
        }}
      >
        {it.icon}
        <ListItemText sx={it.icon ? { ml: 1 } : undefined}>{it.label}</ListItemText>
      </MuiMenuItem>
    ));

  if (isControlled) {
    // Non-modal path: keep the trigger interactive (no backdrop/focus-trap).
    const trigger = isValidElement(children)
      ? cloneElement(children as ReactElement<{ ref?: Ref<HTMLElement> }>, {
          ref: (node: HTMLElement | null) => {
            controlledAnchorRef.current = node;
            assignRef((children as unknown as { ref?: Ref<HTMLElement> }).ref, node);
          },
        })
      : children;
    return (
      <>
        {trigger}
        <Popper
          open={open}
          anchorEl={controlledAnchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
          className={overlayClassName}
        >
          <ClickAwayListener onClickAway={() => onOpenChange?.(false)}>
            {/* Match the trigger width exactly (e.g. the global-search dropdown spans the
                search bar and must NOT grow past it as results load). Fixed width, not
                minWidth; overlayStyle can still override. */}
            <Paper elevation={3} style={{ marginTop: 4, width: controlledAnchorRef.current?.offsetWidth, ...overlayStyle }}>
              {menu?.items ? <MenuList>{renderMenuItems()}</MenuList> : <div onClick={() => onOpenChange?.(false)}>{overlay}</div>}
            </Paper>
          </ClickAwayListener>
        </Popper>
      </>
    );
  }

  // Uncontrolled path: anchor-based MUI Menu (modal, click-away closes it).
  const childOnClick = isValidElement(children)
    ? (children.props as { onClick?: (e: MouseEvent<HTMLElement>) => void }).onClick
    : undefined;
  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ onClick?: (e: MouseEvent<HTMLElement>) => void }>, {
        // merge, don't replace: the trigger's own onClick (e.g. e.preventDefault() on an
        // <a href="#"> sort control) must still run, else clicking navigates the hash.
        onClick: (e: MouseEvent<HTMLElement>) => {
          childOnClick?.(e);
          openMenu(e);
        },
      })
    : children;
  return (
    <>
      {trigger}
      {/* Anchored menu, not a dialog — locking page scroll shifts the layout
          under the cursor each time it opens (see Overlay.tsx). */}
      <MuiMenu anchorEl={anchorEl} open={open} onClose={close} disableScrollLock>
        {menu?.items ? renderMenuItems() : <span onClick={close}>{overlay}</span>}
      </MuiMenu>
    </>
  );
};

export default Dropdown;
